import type { FormattingTabState, PerspectivesEditorState } from '../types';
import {
  createLabelFormatting,
  reorderLabelFormatting,
  updateLabelFormattingAt,
  deleteLabelFormatting,
  createFormattingRule as createFormattingRuleAction,
  deleteFormattingRule as deleteFormattingRuleAction,
  reorderFormattingRules as reorderFormattingRulesAction,
  updateFormattingRule as updateFormattingRuleAction,
  type UpdateFormattingRulePayload,
  showReferenceTypeToggled,
  keepOriginalColorChanged,
  clearLabelFormattingRules as clearLabelFormattingRulesAction,
  clearConditionalFormattingRules as clearConditionalFormattingRulesAction,
} from './actions';
import { type ExtractPayload, reducer } from '@ardoq/rxbeach';
import {
  addNewFormattingRule,
  deleteFormattingRule,
  type FormattingRule,
  updateFormattingRules,
  reorderFormattingRules,
} from '@ardoq/perspectives';
import { type LabelFormattingInfo } from '@ardoq/data-model';
import type { PerspectivesFormattingOptions } from '@ardoq/perspectives-sidebar';
import {
  notifyFormattingOptionsChanged,
  appliedFormattingRulesChanged,
} from 'perspective/actions';
import { getAppliedFormattingState } from './getAppliedFormattingState';
import {
  DEFAULT_COMPONENT_LABEL_FORMATTING,
  DEFAULT_REFERENCE_LABEL_FORMATTING,
} from 'perspective/perspectiveEditor/labelFormattingUtils';

export const getFormattingTabInitialState = (): FormattingTabState => ({
  conditionalFormattingRules: [],
  labelFormatting: [],
  showReferenceType: true,
  keepOriginalColor: true,
  formattingOptions: {
    appliedConditionalFormattingRules: [],
    appliedComponentFormattingLabel: DEFAULT_COMPONENT_LABEL_FORMATTING,
    appliedReferenceFormattingLabel: DEFAULT_REFERENCE_LABEL_FORMATTING,
    appliedLabelFormatting: [],
    appliedShowReferenceType: false,
    componentFormattingCustomLabels: [],
    referenceFormattingCustomLabels: [],
    appliedKeepOriginalColor: true,
  },
});

export const clearFormattingTabState = (
  state: PerspectivesEditorState
): FormattingTabState => ({
  ...state.formattingTab,
  conditionalFormattingRules: [],
  showReferenceType: true,
});

const handleCreateFormattingRule = (
  state: PerspectivesEditorState,
  newFormattingRule: FormattingRule
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    conditionalFormattingRules: addNewFormattingRule(
      state.formattingTab.conditionalFormattingRules,
      newFormattingRule,
      true,
      state.activeViewId
    ),
  },
});

const handleCreateLabelFormatting = (
  state: PerspectivesEditorState,
  newLabelFormatting: LabelFormattingInfo
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    labelFormatting: [
      newLabelFormatting,
      ...(state.formattingTab.labelFormatting ?? []),
    ],
  },
});

const handleReorderLabelFormatting = (
  state: PerspectivesEditorState,
  reorderedLabelFormatting: LabelFormattingInfo[]
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    labelFormatting: reorderedLabelFormatting,
  },
});

const handleDeleteLabelFormatting = (
  state: PerspectivesEditorState,
  deletedLabelFormattingIndex: number
) => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    labelFormatting: state.formattingTab.labelFormatting.toSpliced(
      deletedLabelFormattingIndex,
      1
    ),
  },
});

const handleUpdateLabelFormattingAt = (
  state: PerspectivesEditorState,
  { index, ...rest }: ExtractPayload<typeof updateLabelFormattingAt>
) => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    labelFormatting: state.formattingTab.labelFormatting.toSpliced(index, 1, {
      ...state.formattingTab.labelFormatting[index],
      ...rest,
    }),
  },
});

const handleUpdateFormattingRule = (
  state: PerspectivesEditorState,
  {
    updatedFormattingRuleId,
    updatedFormattingRule,
  }: UpdateFormattingRulePayload
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    conditionalFormattingRules: updateFormattingRules(
      state.formattingTab.conditionalFormattingRules,
      updatedFormattingRuleId,
      updatedFormattingRule
    ),
  },
});

const handleDeleteFormattingRule = (
  state: PerspectivesEditorState,
  deletedFormattingRuleId: string
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    conditionalFormattingRules: deleteFormattingRule(
      state.formattingTab.conditionalFormattingRules,
      deletedFormattingRuleId
    ),
  },
});

const handleReorderFormattingRules = (
  state: PerspectivesEditorState,
  reorderedFormattingRulesIds: string[]
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    conditionalFormattingRules: reorderFormattingRules(
      state.formattingTab.conditionalFormattingRules,
      reorderedFormattingRulesIds
    ),
  },
});

const handleShowReferenceTypeToggled = (
  state: PerspectivesEditorState,
  showReferenceType: boolean
): PerspectivesEditorState => ({
  ...state,
  formattingTab: { ...state.formattingTab, showReferenceType },
});

/**
 * handleNotifyFormattingOptionsChange is emitted by the postMessageBridge and informs that the formattingOptions have changed.
 * This only updates the formattingOptions metadata, NOT the UI rules.
 */
const handleNotifyFormattingOptionsChange = (
  state: PerspectivesEditorState,
  formattingOptions: PerspectivesFormattingOptions
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    formattingOptions,
  },
});

/**
 * handleAppliedFormattingRulesChanged is dispatched when the applied formatting rules have actually changed.
 * This updates the UI rules (conditionalFormattingRules, labelFormatting, showReferenceType, keepOriginalColor).
 * Note: Applied rules are already filtered (no incomplete rules) because they were filtered
 * in getApplicablePerspectiveState before being saved to applied state.
 */
const handleAppliedFormattingRulesChanged = (
  state: PerspectivesEditorState,
  formattingOptions: PerspectivesFormattingOptions
): PerspectivesEditorState => {
  const formattingTabState = getAppliedFormattingState(formattingOptions);

  return {
    ...state,
    formattingTab: {
      ...state.formattingTab,
      ...formattingTabState,
      formattingOptions,
    },
  };
};

const handleKeepOriginalColorChanged = (
  state: PerspectivesEditorState,
  keepOriginalColor: boolean
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    keepOriginalColor,
  },
});

const handleClearLabelFormattingRules = (
  state: PerspectivesEditorState
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    labelFormatting: [],
    showReferenceType: getFormattingTabInitialState().showReferenceType,
  },
});

const handleClearConditionalFormattingRules = (
  state: PerspectivesEditorState
): PerspectivesEditorState => ({
  ...state,
  formattingTab: {
    ...state.formattingTab,
    conditionalFormattingRules: [],
    keepOriginalColor: true,
  },
});

export const formattingTabReducers = [
  reducer(createLabelFormatting, handleCreateLabelFormatting),
  reducer(reorderLabelFormatting, handleReorderLabelFormatting),
  reducer(deleteLabelFormatting, handleDeleteLabelFormatting),
  reducer(updateLabelFormattingAt, handleUpdateLabelFormattingAt),
  reducer(createFormattingRuleAction, handleCreateFormattingRule),
  reducer(updateFormattingRuleAction, handleUpdateFormattingRule),
  reducer(deleteFormattingRuleAction, handleDeleteFormattingRule),
  reducer(reorderFormattingRulesAction, handleReorderFormattingRules),
  reducer(notifyFormattingOptionsChanged, handleNotifyFormattingOptionsChange),
  reducer(appliedFormattingRulesChanged, handleAppliedFormattingRulesChanged),
  reducer(showReferenceTypeToggled, handleShowReferenceTypeToggled),
  reducer(keepOriginalColorChanged, handleKeepOriginalColorChanged),
  reducer(clearLabelFormattingRulesAction, handleClearLabelFormattingRules),
  reducer(
    clearConditionalFormattingRulesAction,
    handleClearConditionalFormattingRules
  ),
];
